import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaPlus, FaDownload, FaSpinner, FaTimes, FaSearch, FaFilePdf, FaTrash, FaExclamationTriangle, FaUserCircle } from 'react-icons/fa';
import PayslipForm from './components/PayslipForm';
import { db, storage } from '../../../firebase/config';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  where, 
  addDoc, 
  serverTimestamp,
  doc,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { sendEmail } from '../../../emailjs/emailService';
import liveServerStatus from '../../../liveServerStatus';
import './Payslips.scss';

const Payslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [payslipToDelete, setPayslipToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Get employees from Redux store
  const { employees } = useSelector(state => state.auth);
  
  // Debug: Log employees data
  useEffect(() => {
    console.log('Employees from Redux store:', employees);
    console.log('Number of employees:', employees?.length || 0);
  }, [employees]);

  useEffect(() => {
    if (selectedEmployee) {
      console.log('Employee selected:', selectedEmployee);
      if (selectedEmployee.email) {
        fetchEmployeePayslips(selectedEmployee.email);
      } else {
        console.error('Selected employee has no email:', selectedEmployee);
        setErrorMessage('Selected employee has no email address');
      }
    } else {
      setPayslips([]);
      setErrorMessage('');
    }
  }, [selectedEmployee]);

  // Send payslip notification email to employee
  const sendPayslipNotificationEmail = async (employee, payMonth, downloadUrl) => {
    try {
      console.log('📧 Sending payslip notification email to:', employee.email);
      
      // Generate HTML email content
      const testNote = !liveServerStatus ? `
        <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; color: #856404; padding: 15px; border-radius: 6px; margin-bottom: 20px; text-align: center;">
          <strong>⚠️ TEST EMAIL - NO ACTION REQUIRED</strong><br>
          <span style="font-size: 14px;">This is a test email from the development environment. Please ignore this notification.</span>
        </div>
      ` : '';

      const mailBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background-color: #51796F; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 24px;">MyelinZ PPC</h1>
            <p style="margin: 10px 0 0 0; font-size: 16px;">Payslip Notification</p>
          </div>
          
          <div style="background-color: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px;">
            ${testNote}
            
            <h2 style="color: #333; margin-bottom: 20px;">Hello ${employee.name || employee.displayName || 'there'},</h2>
            
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
              Your payslip for <strong>${payMonth}</strong> has been uploaded and is now available for download.
            </p>
            
            <div style="background-color: white; padding: 20px; border-radius: 6px; border-left: 4px solid #51796F; margin: 20px 0;">
              <h3 style="color: #51796F; margin: 0 0 10px 0;">Payslip Details:</h3>
              <p style="margin: 5px 0; color: #666;"><strong>Employee ID:</strong> ${employee.empID || 'N/A'}</p>
              <p style="margin: 5px 0; color: #666;"><strong>Pay Period:</strong> ${payMonth}</p>
              <p style="margin: 5px 0; color: #666;"><strong>Upload Date:</strong> ${new Date().toLocaleDateString()}</p>
            </div>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${downloadUrl}" 
                 style="background-color: #51796F; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">
                Download Payslip
              </a>
            </div>
            
            <p style="color: #666; font-size: 14px; line-height: 1.6; margin-top: 30px;">
              If you have any questions about your payslip, please contact the HR department.
            </p>
            
            <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">
            
            <p style="color: #999; font-size: 12px; text-align: center; margin: 0;">
              This is an automated message from MyelinZ PPC System. Please do not reply to this email.
            </p>
          </div>
        </div>
      `;
      
      // Prepare email parameters
      const emailParams = {
        mailSubject: `[MyelinZ PPC] Payslip Available: ${payMonth}`,
        mailBody,
        mailOne: employee.email,
        reply_to: 'hr@myelinz.com'
      };
      
      // Send the email
      const result = await sendEmail(emailParams);
      
      if (result.success) {
        console.log('✅ Payslip notification email sent successfully to:', employee.email);
      } else {
        console.error('❌ Failed to send payslip notification email:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error sending payslip notification email:', error);
      return { success: false, error };
    }
  };

  const fetchEmployeePayslips = async (email) => {
    try {
      setLoading(true);
      setErrorMessage(''); // Clear any previous errors
      
      console.log('Fetching payslips for employee:', email);
      
      const payslipsRef = collection(db, 'payslips');
      const payslipsQuery = query(payslipsRef, where('email', '==', email), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(payslipsQuery);
      
      console.log('Payslips query snapshot:', snapshot.docs.length, 'documents found');
      
      const payslipsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Processed payslips data:', payslipsData);
      setPayslips(payslipsData);
    } catch (error) {
      console.error('Error fetching payslips:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      setErrorMessage(`Failed to fetch payslips: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const showModal = () => {
    if (!selectedEmployee) {
      setErrorMessage('Please select an employee first');
      return;
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Receives a formatted payMonth (YYYY-MMM) and the PDF file from the PayslipForm component
  const handlePayslipSubmit = async (payMonth, pdfFile) => {
    if (!selectedEmployee || !pdfFile || !payMonth) {
      setErrorMessage('Missing required information');
      return;
    }
    
    try {
      setUploading(true);
      
      // Create a reference to the file in Storage
      const fileName = `${payMonth}.pdf`;
      const filePath = `payslips/${selectedEmployee.empID}/${fileName}`;
      const storageRef = ref(storage, filePath);
      
      // Upload the file
      await uploadBytes(storageRef, pdfFile);
      
      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Add the payslip document to Firestore
      const payslipData = {
        createdAt: serverTimestamp(),
        email: selectedEmployee.email,
        empID: selectedEmployee.empID,
        payMonth,
        url: downloadUrl
      };
      
      await addDoc(collection(db, 'payslips'), payslipData);
      
      // Send email notification to employee
      try {
        await sendPayslipNotificationEmail(selectedEmployee, payMonth, downloadUrl);
      } catch (emailError) {
        console.error('Failed to send payslip notification email:', emailError);
        // Don't fail the entire operation if email fails
      }
      
      // Refresh the payslips list
      fetchEmployeePayslips(selectedEmployee.email);
      
      // Close the modal
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error uploading payslip:', error);
      setErrorMessage('Failed to upload payslip');
    } finally {
      setUploading(false);
    }
  };

  const downloadPayslip = (payslip) => {
    if (payslip.url) {
      window.open(payslip.url, '_blank');
    } else {
      setErrorMessage('No download URL available for this payslip');
    }
  };

  const handleDeleteClick = (payslip) => {
    setPayslipToDelete(payslip);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setPayslipToDelete(null);
    setShowDeleteConfirm(false);
  };

  const confirmDelete = async () => {
    if (!payslipToDelete) return;
    
    try {
      setIsDeleting(true);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'payslips', payslipToDelete.id));
      
      // Delete from Storage if we have empID and payMonth
      if (payslipToDelete.empID && payslipToDelete.payMonth) {
        try {
          const fileName = `${payslipToDelete.payMonth}.pdf`;
          const filePath = `payslips/${payslipToDelete.empID}/${fileName}`;
          const storageRef = ref(storage, filePath);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.error('Error deleting file from storage:', storageError);
          // Continue even if storage deletion fails
        }
      }
      
      // Update the UI
      setPayslips(payslips.filter(p => p.id !== payslipToDelete.id));
      
      // Close the confirmation dialog
      setShowDeleteConfirm(false);
      setPayslipToDelete(null);
    } catch (error) {
      console.error('Error deleting payslip:', error);
      setErrorMessage('Failed to delete payslip');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderTableHeader = () => {
    return (
      <div className="payslip-table-header">
        <div className="table-cell month-col">Pay Month</div>
        <div className="table-cell date-col">Created Date</div>
        <div className="table-cell actions-col">Actions</div>
      </div>
    );
  };

  const renderTableRow = (payslip) => {
    const createdDate = payslip.createdAt?.toDate ? 
      payslip.createdAt.toDate().toLocaleDateString() : 
      'N/A';
      
    return (
      <div className="payslip-table-row" key={payslip.id}>
        <div className="table-cell month-col">{payslip.payMonth}</div>
        <div className="table-cell date-col">{createdDate}</div>
        <div className="table-cell actions-col">
          <button 
            className="download-button"
            onClick={() => downloadPayslip(payslip)}
            title="Download payslip"
          >
            <FaDownload className="icon" />
            <span>Download</span>
          </button>
          <button 
            className="delete-button"
            onClick={() => handleDeleteClick(payslip)}
            title="Delete payslip"
          >
            <FaTrash className="icon" />
            <span>Delete</span>
          </button>
        </div>
      </div>
    );
  };

  // Filter employees based on search query
  const filteredEmployees = employees.filter(emp => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      emp.name?.toLowerCase().includes(query) ||
      emp.email?.toLowerCase().includes(query) ||
      emp.empID?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="payslips-container">
      <div className="payslips-layout">
        {/* Employee Sidebar */}
        <div className="employees-sidebar">
          <div className="sidebar-header">
            <h2>Employees</h2>
            <div className="search-input">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search employees..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <div className="employees-list">
            {filteredEmployees.length === 0 ? (
              <div className="no-employees">
                <p>No employees found</p>
              </div>
            ) : (
              filteredEmployees.map(employee => (
                <div 
                  key={employee.id || employee.email} 
                  className={`employee-item ${selectedEmployee?.email === employee.email ? 'selected' : ''}`}
                  onClick={() => setSelectedEmployee(employee)}
                >
                  {employee.img ? (
                    <div className="employee-avatar with-photo">
                      <img src={employee.img} alt={employee.name} />
                    </div>
                  ) : (
                    <div className="employee-avatar">
                      {employee.name ? employee.name.charAt(0).toUpperCase() : 'E'}
                    </div>
                  )}
                  <div className="employee-info">
                    <h3>{employee.name || 'Unknown'}</h3>
                    <p>{employee.email}</p>
                    {employee.empID && <span className="employee-id">{employee.empID}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Payslips Content */}
        <div className="payslips-content">
          {!selectedEmployee ? (
            <div className="no-selection">
              <FaFilePdf className="icon" />
              <h3>Select an employee to view payslips</h3>
              <p>Choose an employee from the list on the left</p>
            </div>
          ) : (
            <>
              <div className="content-header">
                <div className="employee-details">
                  {selectedEmployee.img ? (
                    <div className="employee-photo">
                      <img src={selectedEmployee.img} alt={selectedEmployee.name} />
                    </div>
                  ) : (
                    <div className="employee-photo placeholder">
                      <FaUserCircle />
                    </div>
                  )}
                  <div>
                    <h2>{selectedEmployee.name}'s Payslips</h2>
                    <p className="employee-email">{selectedEmployee.email}</p>
                  </div>
                </div>
                <button 
                  className="add-button"
                  onClick={showModal}
                >
                  <FaPlus className="icon" />
                  <span>Upload Payslip</span>
                </button>
              </div>
              
              {errorMessage && (
                <div className="error-message">{errorMessage}</div>
              )}
              
              <div className="payslips-table">
                {loading ? (
                  <div className="loading-indicator">
                    <FaSpinner className="spinner" />
                    <span>Loading payslips...</span>
                  </div>
                ) : payslips.length === 0 ? (
                  <div className="no-data">
                    <p>No payslips found for this employee</p>
                  </div>
                ) : (
                  <div className="table-container">
                    {renderTableHeader()}
                    <div className="table-body">
                      {payslips.map(payslip => renderTableRow(payslip))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upload Payslip Modal */}
      {isModalVisible && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Upload Payslip for {selectedEmployee?.name}</h3>
              <button className="close-button" onClick={handleCancel}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <PayslipForm 
                employee={selectedEmployee}
                onSubmit={handlePayslipSubmit}
                onCancel={handleCancel}
                uploading={uploading}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-container delete-confirm-modal">
            <div className="modal-header">
              <h3>Delete Payslip</h3>
              <button className="close-button" onClick={cancelDelete}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="delete-confirm-content">
                <div className="warning-icon">
                  <FaExclamationTriangle />
                </div>
                <p>Are you sure you want to delete the payslip for <strong>{payslipToDelete?.payMonth}</strong>?</p>
                <p className="warning-text">This action cannot be undone and will permanently remove the payslip.</p>
                
                <div className="delete-actions">
                  <button 
                    className="cancel-button" 
                    onClick={cancelDelete}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button 
                    className="delete-button" 
                    onClick={confirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <FaSpinner className="spinner" />
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <FaTrash className="icon" />
                        <span>Delete</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payslips; 